// Lightweight stale-while-revalidate cache for instant page revisits.
// Data that was loaded once renders immediately from cache while a fresh copy
// is fetched in the background and swapped in without a spinner.
import { useCallback, useEffect, useRef, useState } from "react";
import { http } from "./api";

const mem = new Map();        // key -> last data
const inflight = new Map();   // key -> in-flight promise (dedupes concurrent loads)
const subs = new Map();       // key -> Set<setter> (cross-component live updates)

function persistRead(key) {
  if (mem.has(key)) return mem.get(key);
  try {
    const raw = sessionStorage.getItem("swr:" + key);
    if (raw != null) {
      const v = JSON.parse(raw);
      mem.set(key, v);
      return v;
    }
  } catch { /* ignore */ }
  return undefined;
}

function cacheWrite(key, data) {
  mem.set(key, data);
  try { sessionStorage.setItem("swr:" + key, JSON.stringify(data)); } catch { /* quota */ }
  const s = subs.get(key);
  if (s) s.forEach((fn) => fn(data));
}

// Seed/overwrite a cache entry (e.g. after a create/update mutation).
export function mutateCache(key, data) {
  if (key && data !== undefined) cacheWrite(key, data);
}

// Synchronously read a cached value (undefined if absent) — used to seed state
// instantly on mount so filtered pages don't flash an empty/loading state.
export function peekCache(key) {
  return key ? persistRead(key) : undefined;
}

// Drop every cached entry whose key starts with `prefix` so the next read refetches.
export function invalidateCache(prefix = "") {
  for (const k of Array.from(mem.keys())) {
    if (k.startsWith(prefix)) {
      mem.delete(k);
      try { sessionStorage.removeItem("swr:" + k); } catch { /* ignore */ }
    }
  }
}

// Deduped background fetch that writes the cache + notifies subscribers.
function revalidateKey(key, fetcher) {
  if (inflight.has(key)) return inflight.get(key);
  const p = Promise.resolve()
    .then(fetcher)
    .then((data) => { cacheWrite(key, data); return data; })
    .finally(() => { inflight.delete(key); });
  inflight.set(key, p);
  return p;
}

/**
 * useCachedData — stale-while-revalidate hook.
 * @param {string|null} key   unique cache key (include filters/branch). null disables.
 * @param {() => Promise<any>} fetcher  returns the data to cache.
 * @returns { data, loading, validating, reload, setData }
 */
export function useCachedData(key, fetcher) {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [data, setData] = useState(() => (key ? persistRead(key) : undefined));
  const [validating, setValidating] = useState(false);
  const keyRef = useRef(key);
  keyRef.current = key;

  const reload = useCallback(() => {
    if (!keyRef.current) return Promise.resolve();
    const k = keyRef.current;
    setValidating(true);
    return revalidateKey(k, () => fetcherRef.current())
      .then((d) => { if (keyRef.current === k) setData(d); return d; })
      .catch(() => {})
      .finally(() => { if (keyRef.current === k) setValidating(false); });
  }, []);

  useEffect(() => {
    if (!key) return undefined;
    if (!subs.has(key)) subs.set(key, new Set());
    subs.get(key).add(setData);
    const cached = persistRead(key);
    if (cached !== undefined) setData(cached);
    reload();
    return () => { const s = subs.get(key); if (s) s.delete(setData); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const loading = data === undefined && validating;
  return { data, loading, validating, reload, setData };
}

// Convenience fetcher for simple GETs.
export const getJSON = (url, config) => http.get(url, config).then((r) => r.data);

// Prefetch a set of [key, url] pairs into the cache (used right after login).
export function prefetch(entries = []) {
  entries.forEach(([key, url]) => {
    if (!mem.has(key)) revalidateKey(key, () => getJSON(url)).catch(() => {});
  });
}
